import { useState, useEffect } from "react";
import { formatTime } from "./utils";
import { IcCoffee, IcCard, IcCash, IcChart, IcTrophy, IcCalendar, IcClock, IcChair } from "./Icons";

// "" → relative URL: dev'de CRA proxy (package.json "proxy"), prod'da Nginx same-origin
const API = process.env.REACT_APP_API_URL ?? "";

function StatBox({ label, value, sub, color = "amber" }) {
  return (
    <div className="db-stat-box">
      <div className="db-stat-label">{label}</div>
      <div className={`db-stat-value color-${color}`}>{value}</div>
      {sub && <div className="db-stat-sub">{sub}</div>}
    </div>
  );
}

function HourBar({ item, max }) {
  const pct = max > 0 ? (item.count / max) * 100 : 0;
  const isNow = new Date().getHours() === item.hour;
  return (
    <div className={`hour-bar${isNow ? " is-now" : ""}`}>
      <div className="hour-bar-label">{item.label}</div>
      <div className="hour-bar-track">
        <div
          className="hour-bar-fill"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="hour-bar-count">{item.count || ""}</div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [txFilter, setTxFilter] = useState(null);   // null | "CASH" | "CARD" | "ALL"
  const [txList, setTxList] = useState([]);
  const [txLoading, setTxLoading] = useState(false);

  const fetchData = async () => {
    try {
      const sig = () => AbortSignal.timeout(8000);
      const [todayRes, weekRes] = await Promise.all([
        fetch(`${API}/api/dashboard/daily`,  { credentials: "include", signal: sig() }),
        fetch(`${API}/api/dashboard/weekly`, { credentials: "include", signal: sig() }),
      ]);
      const todayJson = await todayRes.json();
      const weekJson = await weekRes.json();
      setData(todayJson);
      setWeekly(weekJson);
      setLastRefresh(new Date());
    } catch (e) {
      console.error("Dashboard fetch failed:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const openTxDrawer = async (method) => {
    // Aynı karta tekrar basılırsa drawer kapanır
    if (txFilter === method) { setTxFilter(null); return; }
    setTxFilter(method);
    setTxLoading(true);
    try {
      const param = method === "ALL" ? "" : `&method=${method}`;
      const res = await fetch(
        `${API}/api/dashboard/payments?date=${data.date}${param}`,
        { credentials: "include" }
      );
      const json = await res.json();
      setTxList(json.payments || []);
    } catch { setTxList([]); }
    setTxLoading(false);
  };

  if (loading) {
    return (
      <div className="db-loading">
        <span className="db-loading-icon"><IcCoffee size={28} /></span>
        <span>Veriler yükleniyor…</span>
      </div>
    );
  }

  if (!data) {
    return <div className="db-loading">Veri alınamadı</div>;
  }

  const { summary, hourly, top_products, by_table, payment_breakdown } = data;
  const maxHour = Math.max(...hourly.map((h) => h.count), 1);
  const maxWeekRevenue = weekly
    ? Math.max(...weekly.days.map((d) => d.revenue), 1)
    : 1;

  const today = new Date().toLocaleDateString("tr-TR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  // Payment breakdown (CASH vs CARD)
  const cashAmount = payment_breakdown?.CASH?.amount || 0;
  const cardAmount = payment_breakdown?.CARD?.amount || 0;
  const totalPayments = cashAmount + cardAmount;

  return (
    <div className="dashboard">
      <div className="db-header">
        <div>
          <div className="db-title">Günlük Özet</div>
          <div className="db-date">{today}</div>
        </div>
        <button className="db-refresh-btn" onClick={fetchData}>
          ↻ {lastRefresh?.toLocaleTimeString("tr-TR")}
        </button>
      </div>

      {/* ── Ana Metrikler ── */}
      <div className="db-stats-grid">
        <StatBox
          label="Toplam Sipariş"
          value={summary.total_orders}
          sub={`${summary.done_count} tamamlandı`}
          color="amber"
        />
        <StatBox
          label="Günlük Ciro"
          value={`₺${Math.round(summary.total_revenue).toLocaleString("tr-TR")}`}
          sub={`Ort. ₺${Math.round(summary.avg_order)}/sipariş`}
          color="green"
        />
        <StatBox
          label="Tahmini Kâr"
          value={`₺${Math.round(summary.gross_profit).toLocaleString("tr-TR")}`}
          sub={`Marj %${Math.round(summary.profit_margin)}`}
          color="blue"
        />
        <StatBox
          label="Ort. Hazırlama"
          value={
            summary.avg_prep_minutes > 0
              ? `${Math.round(summary.avg_prep_minutes)}dk`
              : "—"
          }
          sub="sipariş → teslim"
          color="purple"
        />
      </div>

      {/* ── Ödeme Yöntemleri ── */}
      {payment_breakdown && totalPayments > 0 && (
        <div className="db-payment-section">
          <div className="db-section-title"><IcCard size={16} /> Ödeme Yöntemleri</div>
          <div className="db-payment-grid">
            <div
              className={`db-payment-card cash${txFilter === "CASH" ? " active-filter" : ""}`}
              onClick={() => openTxDrawer("CASH")}
            >
              <div className="payment-card-icon"><IcCash size={22} /></div>
              <div className="payment-card-label">Nakit</div>
              <div className="payment-card-amount">₺{Math.round(cashAmount).toLocaleString("tr-TR")}</div>
              <div className="payment-card-sub">{payment_breakdown.CASH?.count || 0} işlem</div>
            </div>
            <div
              className={`db-payment-card card${txFilter === "CARD" ? " active-filter" : ""}`}
              onClick={() => openTxDrawer("CARD")}
            >
              <div className="payment-card-icon"><IcCard size={22} /></div>
              <div className="payment-card-label">Kart</div>
              <div className="payment-card-amount">₺{Math.round(cardAmount).toLocaleString("tr-TR")}</div>
              <div className="payment-card-sub">{payment_breakdown.CARD?.count || 0} işlem</div>
            </div>
            <div
              className={`db-payment-card total${txFilter === "ALL" ? " active-filter" : ""}`}
              onClick={() => openTxDrawer("ALL")}
            >
              <div className="payment-card-icon"><IcChart size={22} /></div>
              <div className="payment-card-label">Toplam</div>
              <div className="payment-card-amount">₺{Math.round(totalPayments).toLocaleString("tr-TR")}</div>
              <div className="payment-card-sub">
                {((payment_breakdown.CASH?.count || 0) + (payment_breakdown.CARD?.count || 0))} işlem
              </div>
            </div>
          </div>

          {/* ── İşlem geçmişi drawer ── */}
          {txFilter && (
            <div className="pay-tx-drawer">
              <div className="pay-tx-header">
                <span className="pay-tx-title" style={{display:"inline-flex",alignItems:"center",gap:6}}>
                  {txFilter === "CASH" ? <><IcCash size={15} /> Nakit</> : txFilter === "CARD" ? <><IcCard size={15} /> Kart</> : <><IcChart size={15} /> Tüm</>} İşlemleri
                </span>
                <button className="pay-tx-close" onClick={() => setTxFilter(null)}>✕</button>
              </div>
              <div className="pay-tx-list">
                {txLoading ? (
                  <div className="pay-tx-empty">Yükleniyor…</div>
                ) : txList.length === 0 ? (
                  <div className="pay-tx-empty">İşlem bulunamadı</div>
                ) : txList.map(tx => (
                  <div key={tx.id} className="pay-tx-row">
                    <div className={`pay-tx-method ${tx.payment_method}`}>
                      {tx.payment_method === "CASH" ? <IcCash size={16} /> : <IcCard size={16} />}
                    </div>
                    <div className="pay-tx-info">
                      <div className="pay-tx-table">{tx.table_label || tx.table_code}</div>
                      <div className="pay-tx-time">
                        {formatTime(tx.created_at)}
                      </div>
                    </div>
                    <div className="pay-tx-amount">
                      ₺{Math.round(tx.amount).toLocaleString("tr-TR")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="db-two-col">
        {/* ── Saatlik Yoğunluk ── */}
        <div className="db-card">
          <div className="db-card-title"><IcClock size={16} /> Saatlik Yoğunluk</div>
          <div className="hour-bars">
            {hourly.map((h) => (
              <HourBar key={h.hour} item={h} max={maxHour} />
            ))}
          </div>
        </div>

        {/* ── En Çok Satanlar ── */}
        <div className="db-card">
          <div className="db-card-title"><IcTrophy size={16} /> En Çok Satanlar</div>
          {top_products.length === 0 ? (
            <div className="db-empty">Henüz sipariş yok</div>
          ) : (
            <div className="top-products">
              {top_products.map((p, i) => (
                <div key={p.product_name} className="top-product-row">
                  <span className="tp-rank">#{i + 1}</span>
                  <span className="tp-emoji">{p.image_emoji || "☕"}</span>
                  <span className="tp-name">{p.product_name}</span>
                  <span className="tp-qty">{p.total_qty} adet</span>
                  <span className="tp-rev">
                    ₺{Math.round(p.total_revenue).toLocaleString("tr-TR")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="db-two-col">
        {/* ── Haftalık Trend ── */}
        <div className="db-card">
          <div className="db-card-title"><IcCalendar size={16} /> Son 7 Gün</div>
          {!weekly || weekly.days.length === 0 ? (
            <div className="db-empty">Yeterli veri yok</div>
          ) : (
            <div className="weekly-bars">
              {weekly.days.map((d) => {
                const pct = (d.revenue / maxWeekRevenue) * 100;
                const dateLabel = new Date(d.date).toLocaleDateString("tr-TR", {
                  weekday: "short",
                  day: "numeric",
                });
                return (
                  <div key={d.date} className="week-bar">
                    <div className="week-bar-track">
                      <div
                        className="week-bar-fill"
                        style={{ height: `${pct}%` }}
                      />
                    </div>
                    <div className="week-bar-rev">
                      ₺{Math.round(d.revenue / 1000 * 10) / 10}k
                    </div>
                    <div className="week-bar-label">{dateLabel}</div>
                    <div className="week-bar-orders">{d.total_orders} sipariş</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Masa Bazlı ── */}
        <div className="db-card">
          <div className="db-card-title"><IcChair size={17} /> Masa Performansı</div>
          {by_table.length === 0 ? (
            <div className="db-empty">Henüz sipariş yok</div>
          ) : (
            <div className="table-perf">
              {by_table.map((t) => {
                const maxRev = by_table[0].revenue;
                const pct = (t.revenue / maxRev) * 100;
                return (
                  <div key={t.table_label} className="table-perf-row">
                    <span className="tpr-name">{t.table_label}</span>
                    <div className="tpr-track">
                      <div className="tpr-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="tpr-count">{t.order_count}x</span>
                    <span className="tpr-rev">
                      ₺{Math.round(t.revenue).toLocaleString("tr-TR")}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
